import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronDown, LogIn, SlidersHorizontal, X, CalendarDays, Star } from "lucide-react";
import { navLinks, LOGIN_URL, ENROLMENTS_URL } from "../data/mock";
import { MENU_ILLUSTRATIONS } from "./MenuIllustrations";
import { SOTD_SCHEDULE_2026 } from "../data/sotdSchedule";
import { getOverride } from "../lib/sotdOverrides";
import SotdMenuThumb from "./SotdMenuThumb";
import SiteSearch from "./SiteSearch";
import DisplayPreferences from "./DisplayPreferences";
import Picture from "./Picture";

const LOGO_URL =
  "/images/dict/vsmall-logo-white.png";

/* Render the illustrated badge for a mega-menu item. Uses one of the
   custom inline SVG tool illustrations from MenuIllustrations.jsx so
   the visual actually represents what the tool does. */
const ItemIllustration = ({ item }) => {
  const Cmp = item.illus && MENU_ILLUSTRATIONS[item.illus];
  if (!Cmp) return null;
  return (
    <span className="bs-mega__item-illus" aria-hidden="true">
      <Cmp />
    </span>
  );
};

/* Icon registry retained for legacy lucide icons (still used by feature
   tile fallback only). */
const ICONS = { Star };
const Icon = ({ name, size = 18, ...rest }) => {
  const Cmp = ICONS[name] || Star;
  return <Cmp size={size} aria-hidden="true" {...rest} />;
};

const Logo = ({ light = false }) => (
  <span className={`bs-brand ${light ? "bs-brand--light" : ""}`}>
    <Picture
      src={LOGO_URL}
      alt="british-sign.co.uk"
      className="bs-brand__logo"
    />
  </span>
);

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/* Beautiful illustrated mega-menu. Used when an item in mock.js has
   `mega: true`, `groups: [...]` and an optional `feature: {...}` block. */
const NavMegaMenu = ({ item, onLinkClick }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const closeTimer = useRef(null);
  const menuId = `nav-megamenu-${slugify(item.label)}`;

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current || wrapRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  /* Keep the mega menu inside the viewport. The CSS centers it under
     the trigger, but when the trigger sits near the right edge on
     mid-size screens the panel would spill off the page. We measure
     after open and slide it back in with an inline transform offset. */
  useEffect(() => {
    if (!open) return undefined;
    const fit = () => {
      const wrap = wrapRef.current;
      const menu = menuRef.current;
      if (!wrap || !menu) return;
      // Skip on mobile drawer (the menu is static / inline below the trigger).
      if (window.matchMedia("(max-width: 960px)").matches) {
        menu.style.removeProperty("--bs-mega-shift");
        return;
      }
      // Compute the shift from the TRIGGER geometry, not the panel's own rect.
      // The panel has a CSS transition on `transform`, and the shift lives in
      // that transform - so reading menu.getBoundingClientRect() right after
      // changing --bs-mega-shift returns the mid-transition (stale) position
      // left over from the previous open. That made the panel land in the
      // wrong place every other time it opened. The trigger wrapper is the
      // menu's offset parent (position: relative) and is never transformed, so
      // its geometry gives the panel's true centered position directly.
      const wrapRect = wrap.getBoundingClientRect();
      const menuW = menu.offsetWidth;
      const naturalLeft = wrapRect.left + wrapRect.width / 2 - menuW / 2;
      const naturalRight = naturalLeft + menuW;
      const margin = 12;
      let shift = 0;
      if (naturalRight > window.innerWidth - margin) {
        shift = window.innerWidth - margin - naturalRight;
      }
      if (naturalLeft + shift < margin) {
        shift = margin - naturalLeft;
      }
      menu.style.setProperty("--bs-mega-shift", `${shift}px`);
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [open]);

  const openMenu = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  };

  const handleLink = () => {
    setOpen(false);
    onLinkClick && onLinkClick();
  };

  const feature = item.feature;

  return (
    <div
      ref={wrapRef}
      className={`bs-nav__group bs-nav__group--mega ${open ? "is-open" : ""}`}
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        className="bs-nav__link bs-nav__trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
        data-testid={`nav-trigger-${slugify(item.label)}`}
      >
        {item.label}
        <ChevronDown size={14} className="bs-nav__chev" aria-hidden="true" />
      </button>
      <div
        ref={menuRef}
        id={menuId}
        className={`bs-mega ${feature ? "bs-mega--with-feature" : ""}`}
        role="menu"
        aria-label={item.label}
      >
        {item.lead && (
          <a
            href={item.lead.href}
            role="menuitem"
            className="bs-mega__lead"
            onClick={handleLink}
            data-testid={`nav-link-${slugify(item.lead.label)}`}
          >
            <span className="bs-mega__lead-text">
              <span className="bs-mega__lead-label">{item.lead.label}</span>
              {item.lead.desc && (
                <span className="bs-mega__lead-desc">{item.lead.desc}</span>
              )}
            </span>
            <ArrowRight size={16} aria-hidden="true" />
          </a>
        )}
        <div className="bs-mega__cols">
          {item.groups.map((g) => (
            <div key={g.title} className="bs-mega__col">
              <div className="bs-mega__col-title" role="heading" aria-level={4}>{g.title}</div>
              <ul className="bs-mega__list">
                {g.items.map((it) => (
                  <li key={it.href}>
                    <a
                      href={it.href}
                      role="menuitem"
                      className="bs-mega__item"
                      onClick={handleLink}
                      data-testid={`nav-link-${slugify(it.label)}`}
                    >
                      <ItemIllustration item={it} />
                      <span className="bs-mega__item-label">{it.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {feature && (
          <a
            href={feature.href}
            className="bs-mega__feature"
            onClick={handleLink}
            data-testid={`nav-feature-${slugify(feature.title)}`}
          >
            <div className="bs-mega__feature-art" aria-hidden="true">
              {feature.preview === "sotd" ? (() => {
                const d = new Date();
                const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                const today = getOverride(key) || SOTD_SCHEDULE_2026[key];
                if (!today) return null;
                return (
                  <div className="bs-mega__feature-sotd">
                    <SotdMenuThumb image={today.image} alt={`The BSL sign for ${today.name}`} />
                    <span className="bs-mega__feature-sotd-word">{today.name}</span>
                  </div>
                );
              })() : feature.preview === "sign-quiz" ? (
                <div className="bs-mega__feature-quiz">
                  <div className="bs-mega__feature-quiz-card">
                    <Picture
                      src="/images/dict/hello22.png"
                      alt=""
                      loading="lazy"
                      draggable={false}
                    />
                  </div>
                  <div className="bs-mega__feature-quiz-options">
                    <span className="bs-mega__feature-quiz-opt">Hello</span>
                    <span className="bs-mega__feature-quiz-opt">Sorry</span>
                    <span className="bs-mega__feature-quiz-opt">Yes</span>
                    <span className="bs-mega__feature-quiz-opt">Please</span>
                  </div>
                </div>
              ) : (() => {
                const FeatCmp = feature.illus && MENU_ILLUSTRATIONS[feature.illus];
                if (FeatCmp) {
                  return (
                    <span className="bs-mega__feature-illus-wrap">
                      <FeatCmp />
                    </span>
                  );
                }
                return (
                  <div className="bs-mega__feature-orb">
                    <Icon name={feature.icon} size={28} />
                  </div>
                );
              })()}
              <span className="bs-mega__feature-grain" />
              <span className="bs-mega__feature-glow" />
            </div>
            <div className="bs-mega__feature-body">
              {feature.eyebrow && (
                <span className="bs-mega__feature-eyebrow">
                  <CalendarDays size={11} aria-hidden="true" />
                  {feature.eyebrow}
                </span>
              )}
              <div className="bs-mega__feature-title" role="heading" aria-level={5}>{feature.title}</div>
              <p className="bs-mega__feature-blurb">{feature.blurb}</p>
              <span className="bs-mega__feature-cta">
                {feature.cta}
                <ArrowRight size={13} aria-hidden="true" />
              </span>
            </div>
          </a>
        )}
      </div>
    </div>
  );
};

const NavDropdown = ({ item, onLinkClick }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);
  const menuId = `nav-menu-${slugify(item.label)}`;

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current || wrapRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const openMenu = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  };

  return (
    <div
      ref={wrapRef}
      className={`bs-nav__group ${open ? "is-open" : ""}`}
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        className="bs-nav__link bs-nav__trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
        data-testid={`nav-trigger-${slugify(item.label)}`}
      >
        {item.label}
        <ChevronDown size={14} className="bs-nav__chev" aria-hidden="true" />
      </button>
      <div
        id={menuId}
        className="bs-nav__menu"
        role="menu"
        aria-label={item.label}
      >
        <div className="bs-nav__menu-inner">
          {item.children.map((c) => (
            <a
              key={c.href}
              href={c.href}
              role="menuitem"
              className="bs-nav__menu-link"
              onClick={() => {
                setOpen(false);
                onLinkClick && onLinkClick();
              }}
              data-testid={`nav-link-${slugify(c.label)}`}
            >
              <span className="bs-nav__menu-label">{c.label}</span>
              {c.desc ? <span className="bs-nav__menu-desc">{c.desc}</span> : null}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  // Header-level "Display & accessibility" popover. Exposes the same
  // controls as the /accessibility page so visitors can adjust skin
  // tone, text size, contrast, motion etc. from any page in one click.
  const [prefsOpen, setPrefsOpen] = useState(false);
  const prefsWrapRef = useRef(null);
  // Header "Login" dropdown: learners → course login, buyers → enrolments.
  const [loginOpen, setLoginOpen] = useState(false);
  const loginWrapRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock background scroll while the mobile nav drawer is open. Without this
  // the page scrolls behind the drawer, so the drawer and the page compete for
  // touch scrolling and the lower menu items become unreachable. With the body
  // locked, the drawer is the only thing that scrolls.
  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add("bs-nav-locked");
    return () => document.body.classList.remove("bs-nav-locked");
  }, [open]);

  // Close the prefs popover on outside click or Escape.
  useEffect(() => {
    if (!prefsOpen) return undefined;
    const handlePointer = (e) => {
      if (prefsWrapRef.current && !prefsWrapRef.current.contains(e.target)) {
        setPrefsOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setPrefsOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [prefsOpen]);

  // Close the login dropdown on outside click or Escape (same pattern as prefs).
  useEffect(() => {
    if (!loginOpen) return undefined;
    const handlePointer = (e) => {
      if (loginWrapRef.current && !loginWrapRef.current.contains(e.target)) {
        setLoginOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setLoginOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [loginOpen]);

  const closeMobile = () => setOpen(false);

  return (
    <>
    <header className={`bs-header ${scrolled ? "is-scrolled" : ""}`}>
      <div className="bs-container bs-header__inner">
        <a href="/" className="bs-brand-link" aria-label="british-sign.co.uk home">
          <Logo />
        </a>

        <nav className={`bs-nav ${open ? "is-open" : ""}`} aria-label="Primary">
          {navLinks.map((l) =>
            // Mega menus render from `groups`; plain dropdowns from `children`.
            // Check `mega` first so a mega item doesn't need a (duplicate)
            // `children` list just to be routed here.
            l.mega ? (
              <NavMegaMenu key={l.label} item={l} onLinkClick={closeMobile} />
            ) : l.children ? (
              <NavDropdown key={l.label} item={l} onLinkClick={closeMobile} />
            ) : (
              <a
                key={l.label}
                href={l.href}
                className="bs-nav__link"
                onClick={closeMobile}
                data-testid={`nav-link-${slugify(l.label)}`}
              >
                {l.label}
              </a>
            )
          )}
          <a
            href={LOGIN_URL}
            className="bs-nav__link bs-nav__link--login"
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeMobile}
          >
            <LogIn size={15} style={{ marginRight: 8, verticalAlign: "-2px" }} />
            Course login
          </a>
          <a
            href={ENROLMENTS_URL}
            className="bs-nav__link bs-nav__link--login bs-nav__link--enrolments"
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeMobile}
          >
            <CalendarDays size={15} style={{ marginRight: 8, verticalAlign: "-2px" }} />
            Manage enrolments
          </a>
        </nav>

        <div className="bs-header__actions">
          <SiteSearch />
          <div
            className="bs-header__prefs"
            ref={prefsWrapRef}
            data-testid="header-prefs-wrap"
          >
            <button
              type="button"
              className="bs-header__prefs-btn"
              aria-label="Display settings: skin tone, text size, contrast"
              title="Display settings"
              aria-expanded={prefsOpen}
              aria-haspopup="dialog"
              onClick={() =>
                setPrefsOpen((v) => {
                  // Mutually exclusive with the mobile nav drawer so the two
                  // overlays never stack on top of each other.
                  if (!v) setOpen(false);
                  return !v;
                })
              }
              data-testid="header-prefs-toggle"
            >
              <SlidersHorizontal size={18} />
            </button>
            {prefsOpen && (
              <div
                className="bs-header__prefs-pop"
                role="dialog"
                aria-label="Display and accessibility settings"
                data-testid="header-prefs-pop"
              >
                <header className="bs-header__prefs-pop-head">
                  <div>
                    <h3 className="bs-header__prefs-pop-h">Display &amp; accessibility</h3>
                    <p className="bs-header__prefs-pop-sub">
                      Settings persist on this device.
                      {" "}<a href="/accessibility" className="bs-header__prefs-pop-link">Display &amp; accessibility settings &rsaquo;</a>
                    </p>
                  </div>
                  <button
                    type="button"
                    className="bs-header__prefs-pop-close"
                    aria-label="Close display settings"
                    onClick={() => setPrefsOpen(false)}
                    data-testid="header-prefs-close"
                  >
                    <X size={16} />
                  </button>
                </header>
                <DisplayPreferences compact className="bs-header__prefs-pop-prefs" />
              </div>
            )}
          </div>
          {/* Two-segment Course pill: a ghost "Login" control for
              existing customers, and a solid primary CTA for new
              visitors, sharing one seam so they read as a single
              grouped control. "Login" opens a small menu because there
              are two returning-customer destinations: learners go to the
              course login, buyers/admins go to the enrolments dashboard. */}
          <div className="bs-header__course" data-testid="header-course-group">
            <div className="bs-header__login-wrap" ref={loginWrapRef}>
              <button
                type="button"
                className="bs-header__course-login"
                aria-haspopup="menu"
                aria-expanded={loginOpen}
                onClick={() => setLoginOpen((v) => !v)}
                data-testid="header-course-login"
              >
                Login
                <ChevronDown size={14} style={{ marginLeft: 5, verticalAlign: "-2px" }} />
              </button>
              {loginOpen && (
                <div className="bs-header__login-pop" role="menu" aria-label="Login">
                  <a
                    href={LOGIN_URL}
                    className="bs-header__login-item"
                    role="menuitem"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setLoginOpen(false)}
                    data-testid="header-login-course"
                  >
                    <span className="bs-header__login-item-label">Course login</span>
                    <span className="bs-header__login-item-desc">Sign in to learn</span>
                  </a>
                  <a
                    href={ENROLMENTS_URL}
                    className="bs-header__login-item"
                    role="menuitem"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setLoginOpen(false)}
                    data-testid="header-login-enrolments"
                  >
                    <span className="bs-header__login-item-label">Manage enrolments</span>
                    <span className="bs-header__login-item-desc">View &amp; assign your seats</span>
                  </a>
                </div>
              )}
            </div>
            <a
              href="/enrol#start"
              className="bs-header__course-cta"
              data-testid="header-start-course-btn"
            >
              Start the course
              <ArrowRight size={14} />
            </a>
          </div>
          <button
            className="bs-burger"
            aria-label="Menu"
            aria-expanded={open}
            onClick={() =>
              setOpen((v) => {
                if (!v) setPrefsOpen(false);
                return !v;
              })
            }
            data-testid="header-burger-btn"
          >
            <span /><span /><span />
          </button>
        </div>
      </div>
    </header>
    </>
  );
};

export default Header;
export { Logo };
